import {Bar, ResponsiveContainer, XAxis, BarChart} from "recharts";
import {Box, Stack, Typography} from "@mui/material";
import {CustomizedAxisTickProps, CustomizedBarProps} from "../../types/recharts";
import {IrrigationPlannerDataProps} from "../../types/irrigationPlannerDataProps";
import {useTranslation} from "react-i18next";
import dayjs from "dayjs";
import {HelpPopover} from "../base/HelpPopover";

export const FieldIrrigationPlannerCard: React.FC<{data: IrrigationPlannerDataProps[]}> = ({data}) => {
    const {t} = useTranslation();
    const ValueAxisTick = ValueAxisTickFn(data);

    return (
        <Stack
            direction="column"
            alignItems="center"
            bgcolor="#F3F5FA"
            padding="1.5rem"
            border={1}
            borderColor="#E9EBF1 !important"
            minWidth="15rem"
            gap="1.25rem"
            flex="1"
            position="relative"
        >
            <Typography textTransform="uppercase" fontSize={16} fontWeight={500}>
                {t("insights.field_irrigation_planner.planner")}
            </Typography>
            <Stack direction="row" width="100%" height={200}>
                <Stack direction="column-reverse" textAlign="right">
                    <Typography fontSize={14} fontWeight={500} textTransform="uppercase" color="#737990">
                        {t("insights.field_irrigation_planner.precipitation")}
                    </Typography>
                    <Typography fontSize={14} fontWeight={500} textTransform="uppercase" color="primary">
                        {t("insights.field_irrigation_planner.irrigation")}
                    </Typography>
                </Stack>
                <ResponsiveContainer>
                    <BarChart data={data} margin={{bottom: 20, left: 0}}>
                        <Bar
                            type="monotone"
                            dataKey="irrigationForecast"
                            yAxisId="left"
                            fill="#5161C1"
                            shape={<DataBar />}
                            barSize={18}
                            isAnimationActive={false}
                        />
                        <Bar
                            type="monotone"
                            dataKey="precipitationForecast"
                            yAxisId="left"
                            fill="#CACDD8"
                            shape={<DataBar />}
                            barSize={18}
                            isAnimationActive={false}
                        />
                        <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={<DateAxisTick />}
                            tickFormatter={formatXAxis}
                            orientation="top"
                        />
                        <XAxis
                            dataKey="date"
                            axisLine={false}
                            tickLine={false}
                            tick={<ValueAxisTick />}
                            tickFormatter={formatXAxis}
                            orientation="bottom"
                            xAxisId="values"
                        />
                    </BarChart>
                </ResponsiveContainer>
            </Stack>
            <Box sx={{position: "absolute", right: "0", top: "0"}}>
                <HelpPopover i18nKey="help.popover.fields.irrigation_planner" placement="top" direction="left" />
            </Box>
        </Stack>
    );
};

const DateAxisTick: React.FC = (props) => {
    const {x, y, payload} = props as CustomizedAxisTickProps;

    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} textAnchor="middle" fill="#737990" fontSize={14}>
                {formatXAxis(payload.value)}
            </text>
        </g>
    );
};

const ValueAxisTickFn = (data: IrrigationPlannerDataProps[]) => {
    const ValueAxisTick: React.FC = (props) => {
        const {x, y, index} = props as CustomizedAxisTickProps;
        const irrigation = data[index].irrigationForecast;
        const precipitation = data[index].precipitationForecast;

        return (
            <g transform={`translate(${x},${y})`}>
                <text
                    x={0}
                    y={15}
                    textAnchor="middle"
                    fill={irrigation === 0 ? "#BDC0CE" : "#5161C1"}
                    fontSize={14}
                    fontWeight={irrigation === 0 ? 500 : 700}
                >
                    {Number(irrigation).toFixed(1)}
                </text>
                <text
                    x={0}
                    y={35}
                    textAnchor="middle"
                    fill={precipitation === 0 ? "#BDC0CE" : "#737990"}
                    fontSize={14}
                    fontWeight={500}
                >
                    {Number(precipitation).toFixed(1)}
                </text>
            </g>
        );
    };

    return ValueAxisTick;
};

const DataBar: React.FC = (props) => {
    const {fill, x, y, width, height} = props as CustomizedBarProps;

    return (
        <svg>
            <rect x={x} y={y + height - 1} width={width} height="2" stroke="none" fill="#CACDD8" />
            <rect x={x} y={y} width={width} height={height} stroke="none" fill={fill} />
        </svg>
    );
};

const formatXAxis = (tick: Date) => {
    return dayjs(tick).format("MMM D").toUpperCase();
};
